import { useRef, useState } from 'react'
import {
  Alert,
  App as AntdApp,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
  Upload,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { BankOutlined, CheckCircleOutlined, ImportOutlined, MailOutlined, PlusOutlined, TeamOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

interface Customer {
  id: number
  customer_no: string
  kind: 'person' | 'company'
  first_name?: string
  last_name?: string
  company?: string
  email?: string
  phone?: string
  active: boolean
  pseudonymized_at?: string
  created_at: string
}

interface ListResp {
  data: {
    items: Customer[]
    total: number
    limit: number
    offset: number
  }
}

interface TagDef {
  id: number
  slug: string
  label: string
  color?: string | null
}

export default function CustomersPage() {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tagFilter, setTagFilter] = useState<number | undefined>(undefined)
  const [importOpen, setImportOpen] = useState(false)
  const [importResult, setImportResult] = useState<{ created: number; skipped: number; errors: { row: number; reason: string }[] } | null>(null)
  const importFileRef = useRef<File | null>(null)
  const [classicImportOpen, setClassicImportOpen] = useState(false)
  const [classicImportResult, setClassicImportResult] = useState<{
    target_id: number; customer_no: string; action: string;
    invoices_new: number; invoices_synced: number;
  } | null>(null)
  const [classicConnInfo, setClassicConnInfo] = useState<{
    mysql_version: string; customer_count: number; invoice_count: number; already_imported: number;
  } | null>(null)
  const [classicForm] = Form.useForm()
  const [selectedIDs, setSelectedIDs] = useState<number[]>([])
  const [bulkTagOpen, setBulkTagOpen] = useState(false)
  const [bulkTagForm] = Form.useForm()
  const [bulkMailOpen, setBulkMailOpen] = useState(false)
  const [bulkMailForm] = Form.useForm()

  const tagsQ = useQuery({
    queryKey: ['customer-tags-list'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: TagDef[] }>('/api/v1/customer-tags')
      return res.data.data || []
    },
  })

  const { data, isLoading } = useQuery({
    queryKey: ['customers', tagFilter],
    queryFn: async () => {
      // DataTable filtert client-seitig über die geladenen Zeilen → alle
      // Kunden laden (Backend-Max 5000), damit die Suche jeden findet.
      const params: Record<string, string | number> = { limit: 5000 }
      if (tagFilter) params.tag_id = tagFilter
      const res = await apiClient.get<ListResp>('/api/v1/customers', { params })
      return res.data.data
    },
  })

  // Customer→Tags lookup. Fetched in bulk for visible rows after the
  // customer-list arrives. Keyed by customer.id; each value is the
  // array of tags. Empty arrays / no-key both render "no tags".
  const customerTagsQ = useQuery({
    enabled: !!data?.items?.length,
    queryKey: ['customers-tags-bulk', data?.items?.map(i => i.id).join(',')],
    queryFn: async () => {
      const ids = data!.items.map(i => i.id)
      const out: Record<number, TagDef[]> = {}
      await Promise.all(ids.map(async (id) => {
        try {
          const r = await apiClient.get<{ data: TagDef[] }>(`/api/v1/customers/${id}/tags`)
          out[id] = r.data.data || []
        } catch { /* ignore single failures */ }
      }))
      return out
    },
  })

  const bulkTag = useMutation({
    mutationFn: async (vals: { add_tag_ids?: number[]; remove_tag_ids?: number[] }) => {
      await apiClient.post('/api/v1/customers/bulk/tag', {
        customer_ids: selectedIDs,
        add_tag_ids: vals.add_tag_ids || [],
        remove_tag_ids: vals.remove_tag_ids || [],
      })
    },
    onSuccess: () => {
      message.success(t('customers.bulk.tagsDone', 'Tags aktualisiert'))
      qc.invalidateQueries({ queryKey: ['customers'] })
      qc.invalidateQueries({ queryKey: ['customers-tags-bulk'] })
      setBulkTagOpen(false)
      bulkTagForm.resetFields()
      setSelectedIDs([])
    },
  })

  const bulkMarkActive = useMutation({
    mutationFn: async (active: boolean) => {
      await apiClient.post('/api/v1/customers/bulk/mark-active', {
        customer_ids: selectedIDs,
        active,
      })
    },
    onSuccess: (_d, active) => {
      message.success(active
        ? t('customers.bulk.activated', '{{n}} Kunden aktiviert', { n: selectedIDs.length })
        : t('customers.bulk.deactivated', '{{n}} Kunden deaktiviert', { n: selectedIDs.length }))
      qc.invalidateQueries({ queryKey: ['customers'] })
      setSelectedIDs([])
    },
  })

  const bulkSendMail = useMutation({
    mutationFn: async (vals: { subject: string; body: string }) => {
      const res = await apiClient.post<{ data: { success_count: number; total: number } }>(
        '/api/v1/customers/bulk/send-mail',
        { customer_ids: selectedIDs, ...vals },
      )
      return res.data.data
    },
    onSuccess: (d) => {
      message.success(
        t('customers.bulk.mailDone', '{{n}}/{{t}} E-Mails eingereiht', { n: d.success_count, t: d.total }),
      )
      setBulkMailOpen(false)
      bulkMailForm.resetFields()
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || t('customers.bulk.mailFailed', 'E-Mail-Versand fehlgeschlagen')),
  })

  const classicTestMut = useMutation({
    mutationFn: async (mysql_dsn?: string) => {
      const res = await apiClient.post<{ data: {
        mysql_version: string; customer_count: number; invoice_count: number; already_imported: number;
      } }>('/api/v1/admin/import/test-connection', { mysql_dsn: mysql_dsn || undefined })
      return res.data.data
    },
    onSuccess: (data) => setClassicConnInfo(data),
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || 'Verbindungstest fehlgeschlagen'),
  })

  const classicImportMut = useMutation({
    mutationFn: async (vals: { old_id: number; mysql_dsn?: string }) => {
      const res = await apiClient.post<{ data: {
        target_id: number; customer_no: string; action: string;
        invoices_new: number; invoices_synced: number;
      } }>('/api/v1/admin/import/customer', vals)
      return res.data.data
    },
    onSuccess: (data) => {
      setClassicImportResult(data)
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || 'Import fehlgeschlagen'),
  })


  const importMut = useMutation({
    mutationFn: async () => {
      const file = importFileRef.current
      if (!file) throw new Error('no file selected')
      const fd = new FormData()
      fd.append('file', file)
      const res = await apiClient.post<{ data: { created: number; skipped: number; errors: { row: number; reason: string }[] } }>(
        '/api/v1/customers/import',
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      )
      return res.data.data
    },
    onSuccess: (data) => {
      setImportResult(data)
      qc.invalidateQueries({ queryKey: ['customers'] })
    },
    onError: (e: { response?: { data?: { error?: string } } }) =>
      message.error(e.response?.data?.error || 'Import fehlgeschlagen'),
  })

  const displayName = (r: Customer) =>
    r.kind === 'company'
      ? r.company || '—'
      : [r.first_name, r.last_name].filter(Boolean).join(' ') || '—'

  const statusTag = (r: Customer) =>
    r.pseudonymized_at ? (
      <Tag color="default">{t('customers.status.pseudonymized')}</Tag>
    ) : r.active ? (
      <Tag color="success">{t('customers.status.active')}</Tag>
    ) : (
      <Tag color="warning">{t('customers.status.inactive')}</Tag>
    )

  const columns: ColumnsType<Customer> = [
    {
      title: t('customers.columns.customerNo'),
      dataIndex: 'customer_no',
      key: 'customer_no',
      width: 130,
      render: (v: string, r: Customer) => (
        <Link to={`/customers/${r.id}`}>
          <Typography.Text code>{v}</Typography.Text>
        </Link>
      ),
    },
    {
      title: t('customers.columns.name'),
      key: 'name',
      render: (_: unknown, r: Customer) => (
        <Link to={`/customers/${r.id}`} className="font-medium">
          {displayName(r)}
        </Link>
      ),
    },
    {
      title: t('customers.columns.email'),
      dataIndex: 'email',
      key: 'email',
      render: (v?: string) => (
        <Typography.Text type="secondary" className="text-base">{v || '—'}</Typography.Text>
      ),
    },
    {
      title: t('customers.columns.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 100,
      render: (k: string) => (
        <Tag color={k === 'company' ? 'blue' : 'green'} icon={k === 'company' ? <TeamOutlined /> : <UserOutlined />}>
          {t(`customers.kind.${k}`)}
        </Tag>
      ),
    },
    {
      title: t('customers.columns.tags', 'Tags'),
      key: 'tags',
      width: 200,
      render: (_: unknown, r: Customer) => {
        const tags = customerTagsQ.data?.[r.id] || []
        if (tags.length === 0) return <Typography.Text type="secondary">—</Typography.Text>
        return (
          <Space size={4} wrap>
            {tags.slice(0, 3).map(tg => (
              <Tag key={tg.id} color={tg.color || undefined}>{tg.label}</Tag>
            ))}
            {tags.length > 3 && (
              <Tag>+{tags.length - 3}</Tag>
            )}
          </Space>
        )
      },
    },
    {
      title: t('customers.columns.status'),
      key: 'status',
      width: 120,
      render: (_: unknown, r: Customer) => statusTag(r),
    },
  ]

  const renderMobileCard = (r: Customer) => {
    const tags = customerTagsQ.data?.[r.id] || []
    return (
      <div className="mob-card">
        <div className="flex-between items-start mb-6">
          <Link to={`/customers/${r.id}`} className="font-semibold text-14 flex-1 mr-8">
            {displayName(r)}
          </Link>
          {statusTag(r)}
        </div>
        <div className="flex-row flex-wrap gap-12 items-center">
          <Typography.Text type="secondary" className="text-sm">
            <Typography.Text code className="text-xs">{r.customer_no}</Typography.Text>
          </Typography.Text>
          {r.email && (
            <Typography.Text type="secondary" className="text-sm">{r.email}</Typography.Text>
          )}
        </div>
        {tags.length > 0 && (
          <Space size={4} wrap className="mt-6">
            {tags.slice(0, 4).map(tg => (
              <Tag key={tg.id} color={tg.color || undefined} className="text-xs">{tg.label}</Tag>
            ))}
            {tags.length > 4 && <Tag className="text-xs">+{tags.length - 4}</Tag>}
          </Space>
        )}
      </div>
    )
  }

  return (
    <>
      <PageHeader
        icon={<TeamOutlined />}
        title={t('customers.title')} subtitle={t('customers.subtitle', 'Kundenstamm, Saldo und Vertragsverwaltung.')}
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder={t('customers.filterByTag', 'Tag-Filter')}
              className="min-w-160"
              value={tagFilter}
              onChange={(v) => setTagFilter(v)}
              loading={tagsQ.isLoading}
              options={(tagsQ.data || []).map(tg => ({
                value: tg.id,
                label: tg.label,
              }))}
            />
            <Button href="/api/v1/customers/export.csv">
              {t('customers.exportCsv', 'CSV-Export')}
            </Button>
            <Button icon={<ImportOutlined />} onClick={() => { setImportResult(null); setImportOpen(true) }}>
              {t('customers.importCsv', 'CSV-Import')}
            </Button>
            <Button icon={<ImportOutlined />} onClick={() => { setClassicImportResult(null); classicForm.resetFields(); setClassicImportOpen(true) }}>
              Import aus Classic
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/customers/new')}
            >
              {t('customers.newCustomer')}
            </Button>
          </Space>
        }
      />
      {(() => {
        const items = data?.items || []
        const companies = items.filter(c => c.kind === 'company').length
        const persons = items.filter(c => c.kind === 'person').length
        const active = items.filter(c => c.active).length
        const total = data?.total ?? 0
        return (
          <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile">
              <span className="kpi-tile-label"><TeamOutlined /> Kunden gesamt</span>
              <span className="kpi-tile-value">{total}</span>
              <span className="kpi-tile-sub">{items.length} angezeigt</span>
            </div>
            <div className={`kpi-tile ${active > 0 ? 'kpi-success' : ''}`}>
              <span className="kpi-tile-label"><CheckCircleOutlined /> Aktiv</span>
              <span className="kpi-tile-value">{active}</span>
              <span className="kpi-tile-sub">aus aktueller Ansicht</span>
            </div>
            <div className="kpi-tile kpi-info">
              <span className="kpi-tile-label"><BankOutlined /> Firmen</span>
              <span className="kpi-tile-value">{companies}</span>
              <span className="kpi-tile-sub">aus aktueller Ansicht</span>
            </div>
            <div className="kpi-tile">
              <span className="kpi-tile-label"><UserOutlined /> Privatpersonen</span>
              <span className="kpi-tile-value">{persons}</span>
              <span className="kpi-tile-sub">aus aktueller Ansicht</span>
            </div>
          </div>
        )
      })()}

      {selectedIDs.length > 0 && (
        <Card size="small" className="content-card mb-12"
          style={{ background: '#FFF7E6', borderColor: '#FFC069' }}>
          <Space wrap>
            <Typography.Text strong>
              {t('customers.bulk.selected', '{{n}} ausgewählt', { n: selectedIDs.length })}
            </Typography.Text>
            <Button onClick={() => setBulkTagOpen(true)}>
              {t('customers.bulk.tags', 'Tags zuweisen')}
            </Button>
            <Button onClick={() => bulkMarkActive.mutate(true)}>
              {t('customers.bulk.activate', 'Aktivieren')}
            </Button>
            <Button onClick={() => bulkMarkActive.mutate(false)}>
              {t('customers.bulk.deactivate', 'Deaktivieren')}
            </Button>
            <Button icon={<MailOutlined />} onClick={() => setBulkMailOpen(true)}>
              {t('customers.bulk.mail', 'E-Mail senden')}
            </Button>
            <Button type="text" onClick={() => setSelectedIDs([])}>
              {t('customers.bulk.clear', 'Auswahl löschen')}
            </Button>
          </Space>
        </Card>
      )}

      <Card className="content-card">
        <DataTable<Customer>
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={data?.items || []}
          searchPlaceholder={t('customers.searchPlaceholder')}
          pageSize={50}
          renderMobileCard={renderMobileCard}
          rowSelection={{
            selectedRowKeys: selectedIDs,
            onChange: (keys) => setSelectedIDs(keys as number[]),
          }}
        />
      </Card>

      <Modal open={bulkTagOpen} onCancel={() => setBulkTagOpen(false)}
        title={t('customers.bulk.tagsTitle', 'Tags auf {{n}} Kunden anwenden', { n: selectedIDs.length })}
        onOk={() => bulkTagForm.submit()}
        confirmLoading={bulkTag.isPending}
        destroyOnClose>
        <Form form={bulkTagForm} layout="vertical"
          onFinish={(v) => bulkTag.mutate({
            add_tag_ids: v.add as number[] | undefined,
            remove_tag_ids: v.remove as number[] | undefined,
          })}>
          <Form.Item name="add" label={t('customers.bulk.addTags', 'Tags hinzufügen')}>
            <Select mode="multiple"
              options={(tagsQ.data || []).map(tg => ({
                value: tg.id, label: tg.label,
              }))} />
          </Form.Item>
          <Form.Item name="remove" label={t('customers.bulk.removeTags', 'Tags entfernen')}>
            <Select mode="multiple"
              options={(tagsQ.data || []).map(tg => ({
                value: tg.id, label: tg.label,
              }))} />
          </Form.Item>
          <Typography.Text type="secondary" className="text-sm">
            {t('customers.bulk.tagsHint',
              'Beide Felder können kombiniert werden. „Hinzufügen" + „Entfernen" auf dem selben Tag ist no-op.')}
          </Typography.Text>
        </Form>
      </Modal>


      <Modal
        open={importOpen}
        title={t('customers.importModal.title', 'Kunden per CSV importieren')}
        onCancel={() => { setImportOpen(false); setImportResult(null); importFileRef.current = null }}
        onOk={() => importMut.mutate()}
        okText={t('customers.importModal.ok', 'Importieren')}
        okButtonProps={{ loading: importMut.isPending, disabled: !!importResult }}
        destroyOnClose
      >
        {!importResult ? (
          <>
            <Typography.Paragraph type="secondary">
              {t('customers.importModal.hint',
                'CSV-Format: kind (person|company), first_name, last_name, company, email, phone, tax_id, notes. Erste Zeile muss die Spaltenüberschriften enthalten.')}
            </Typography.Paragraph>
            <Upload.Dragger
              accept=".csv,text/csv"
              maxCount={1}
              beforeUpload={(file) => { importFileRef.current = file; return false }}
              onRemove={() => { importFileRef.current = null }}
            >
              <p className="ant-upload-drag-icon"><UploadOutlined /></p>
              <p className="ant-upload-text">{t('customers.importModal.drop', 'CSV-Datei hier ablegen oder klicken')}</p>
            </Upload.Dragger>
          </>
        ) : (
          <Space direction="vertical" className="w-full">
            <Alert
              type={importResult.skipped === 0 ? 'success' : 'warning'}
              message={t('customers.importModal.result',
                `${importResult.created} importiert, ${importResult.skipped} übersprungen`)}
            />
            {importResult.errors.length > 0 && (
              <DataTable
                size="small"
                dataSource={importResult.errors.map((e, i) => ({ ...e, key: i }))}
                columns={[
                  { title: 'Zeile', dataIndex: 'row', width: 60 },
                  { title: 'Fehler', dataIndex: 'reason' },
                ]}
                renderMobileCard={(r: { row: number; reason: string }) => (
                  <div className="mob-card mob-card--urgent">
                    <span>Zeile {r.row}: {r.reason}</span>
                  </div>
                )}
              />
            )}
          </Space>
        )}
      </Modal>

      {/* ── Import aus NetCell Control Classic ───────────────────── */}
      <Modal
        open={classicImportOpen}
        title={<><ImportOutlined /> Import aus NetCell Control Classic</>}
        onCancel={() => {
          setClassicImportOpen(false)
          setClassicImportResult(null)
          setClassicConnInfo(null)
        }}
        onOk={() => classicForm.submit()}
        okText="Importieren"
        okButtonProps={{ loading: classicImportMut.isPending, disabled: !!classicImportResult }}
        destroyOnClose
        width={520}
      >
        {!classicImportResult ? (
          <>
            <Typography.Paragraph type="secondary" className="mb-16">
              Importiert einen einzelnen Kunden (Stammdaten + Rechnungsadresse + Rechnungen)
              aus dem alten MySQL-System. Bereits importierte Kunden werden synchronisiert,
              nicht doppelt angelegt.
            </Typography.Paragraph>
            <Form
              form={classicForm}
              layout="vertical"
              onFinish={(v) => classicImportMut.mutate({
                old_id: Number(v.old_id),
                mysql_dsn: v.mysql_dsn || undefined,
              })}
            >
              <Form.Item
                name="mysql_dsn"
                label="MySQL-DSN (optional)"
                extra={'Leer lassen → Wert aus Einstellungen (classic_import_mysql_dsn) wird verwendet'}
              >
                <Space.Compact className="w-full">
                  <Input.Password
                    placeholder="user:pass@tcp(host:3306)/netcell_classic"
                    autoComplete="off"
                  />
                  <Button
                    loading={classicTestMut.isPending}
                    onClick={() => classicTestMut.mutate(classicForm.getFieldValue('mysql_dsn') || undefined)}
                  >
                    Testen
                  </Button>
                </Space.Compact>
              </Form.Item>
              {classicConnInfo && (
                <Alert
                  type="success"
                  className="mb-16"
                  message={`Verbunden — MySQL ${classicConnInfo.mysql_version}`}
                  description={`${classicConnInfo.customer_count} Kunden · ${classicConnInfo.invoice_count} Rechnungen · ${classicConnInfo.already_imported} bereits importiert`}
                  showIcon
                />
              )}
              <Form.Item
                name="old_id"
                label="Alte Kunden-ID (MySQL)"
                rules={[{ required: true, message: 'Pflichtfeld' }]}
                extra="Die numerische ID des Kunden im alten NetCell Control (z. B. 42)"
              >
                <Input placeholder="42" type="number" />
              </Form.Item>
            </Form>
          </>
        ) : (
          <Space direction="vertical" className="w-full">
            <Alert
              type={classicImportResult.action.includes('created') ? 'success' : 'info'}
              message={
                classicImportResult.action.includes('created')
                  ? `Kunde ${classicImportResult.customer_no} neu angelegt (ID ${classicImportResult.target_id})`
                  : `Kunde ${classicImportResult.customer_no} synchronisiert (ID ${classicImportResult.target_id})`
              }
              description={
                `Rechnungen: ${classicImportResult.invoices_new} neu importiert, ${classicImportResult.invoices_synced} synchronisiert`
              }
              showIcon
            />
            <Button
              type="link"
              href={`/customers/${classicImportResult.target_id}`}
              className="p-0"
            >
              Zum Kundenprofil →
            </Button>
          </Space>
        )}
      </Modal>

      <Modal
        open={bulkMailOpen}
        onCancel={() => { setBulkMailOpen(false); bulkMailForm.resetFields() }}
        title={<><MailOutlined /> {t('customers.bulk.mailTitle', 'E-Mail an {{n}} Kunden senden', { n: selectedIDs.length })}</>}
        onOk={() => bulkMailForm.submit()}
        confirmLoading={bulkSendMail.isPending}
        okText={t('customers.bulk.mailSend', 'Senden')}
        destroyOnClose
      >
        <Form form={bulkMailForm} layout="vertical"
          onFinish={(v) => bulkSendMail.mutate({ subject: v.subject, body: v.body })}
        >
          <Form.Item name="subject" label={t('customers.bulk.mailSubject', 'Betreff')}
            rules={[{ required: true, message: t('form.required', 'Pflichtfeld') }]}>
            <Input />
          </Form.Item>
          <Form.Item name="body" label={t('customers.bulk.mailBody', 'Nachricht')}
            rules={[{ required: true, message: t('form.required', 'Pflichtfeld') }]}>
            <Input.TextArea rows={6} />
          </Form.Item>
          <Typography.Text type="secondary" className="text-sm">
            {t('customers.bulk.mailNote',
              'Die E-Mail wird an die primäre E-Mail-Adresse jedes ausgewählten Kunden gesendet. Kunden ohne E-Mail-Adresse werden übersprungen.')}
          </Typography.Text>
        </Form>
      </Modal>
    </>
  )
}
